import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
	Card,
	CardBody,
	Row,
	Col,
	Label,
	Input,
	Button,
	Alert,
	UncontrolledTooltip,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Badge,
} from "reactstrap";
import { PostData, GetRequest } from "../../../services/FetchData";
import Path from "../../../services/Path";
import { ProductContext } from "../../../store";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import IntegrationBrandLogo from "../../../components/Integrations/IntegrationBrandLogo";
import WordPressIntegrationModal from "./WordPressIntegrationModal";
import ShopifyIntegrationModal from "./ShopifyIntegrationModal";
import { getWordPressIntegration } from "../../../services/wordpressIntegrationApi";
import { getShopifyIntegration } from "../../../services/shopifyIntegrationApi";

const NETSUITE_API = "api/v1/netsuite/";

const OAUTH_ERROR_MESSAGES = {
	not_configured: "Save your Connected App credentials before connecting.",
	missing_params: "Salesforce did not return a valid authorization code.",
	invalid_state: "OAuth session expired. Try connecting again.",
	invalid_website: "Invalid website. Try again.",
	token_exchange: "Could not exchange the authorization code. Try again.",
	config: "Salesforce credentials are missing. Save Client ID and Secret first.",
	callback: "Something went wrong during Salesforce callback.",
};

const NETSUITE_OAUTH_ERROR_MESSAGES = {
	not_configured: "Save your NetSuite integration credentials before connecting.",
	missing_params: "NetSuite did not return a valid authorization code.",
	invalid_state: "OAuth session expired. Try connecting again.",
	token_exchange: "Could not exchange the authorization code. Check Client ID, Secret, and redirect URL in NetSuite.",
	callback: "Something went wrong during NetSuite callback.",
	access_denied: "NetSuite authorization was cancelled.",
};

const ZOHO_OAUTH_ERROR_MESSAGES = {
	not_configured: "Save your Zoho Connected App credentials before connecting.",
	missing_params: "Zoho did not return a valid authorization code.",
	invalid_state: "OAuth session expired. Try connecting again.",
	invalid_website: "Invalid website. Try again.",
	token_exchange: "Could not exchange the authorization code. Try again.",
	config: "Zoho credentials are missing. Save Client ID and Secret first.",
	callback: "Something went wrong during Zoho callback.",
	access_denied: "Zoho authorization was cancelled.",
};

const Integrations = () => {
	const { currentWebsiteId, userLoader, websiteData } = useContext(ProductContext);
	const location = useLocation();
	const navigate = useNavigate();
	const websiteId = currentWebsiteId || Cookies.get("_website_id");
	const websiteType = String(websiteData?.[websiteId]?.websiteType || "").toLowerCase();
	const isWordPress = websiteType === "wordpress";
	const isShopify = websiteType === "shopify";

	const [pageLoader, setPageLoader] = useState(true);
	const [statusLoading, setStatusLoading] = useState(false);
	const [saveLoading, setSaveLoading] = useState(false);
	const [disconnectLoading, setDisconnectLoading] = useState(false);

	const [connected, setConnected] = useState(false);
	const [configured, setConfigured] = useState(false);
	const [orgName, setOrgName] = useState("");
	const [sfApiPerms, setSfApiPerms] = useState(null);

	const [clientId, setClientId] = useState("");
	const [clientSecret, setClientSecret] = useState("");
	const [env, setEnv] = useState("production");
	const [caseQueueId, setCaseQueueId] = useState("");

	const [nsStatusLoading, setNsStatusLoading] = useState(false);
	const [nsSaveConnectLoading, setNsSaveConnectLoading] = useState(false);
	const [nsTestLoading, setNsTestLoading] = useState(false);
	const [nsDisconnectLoading, setNsDisconnectLoading] = useState(false);
	const [nsConnected, setNsConnected] = useState(false);
	const [nsConfigured, setNsConfigured] = useState(false);
	const [nsRedirectUrl, setNsRedirectUrl] = useState("");
	const [nsAccountMasked, setNsAccountMasked] = useState("");
	const [nsTokenExpiresAt, setNsTokenExpiresAt] = useState(null);
	const [nsClientId, setNsClientId] = useState("");
	const [nsClientSecret, setNsClientSecret] = useState("");
	const [nsAccountId, setNsAccountId] = useState("");
	const [nsEnvironment, setNsEnvironment] = useState("production");
	const [nsAuthMode, setNsAuthMode] = useState("authorization_code");
	const [nsCertificateKid, setNsCertificateKid] = useState("");
	const [nsPrivateKeyPem, setNsPrivateKeyPem] = useState("");
	const [nsCertificatePem, setNsCertificatePem] = useState("");
	const [nsCertificateExpiresAt, setNsCertificateExpiresAt] = useState(null);
	const [nsM2mSaveLoading, setNsM2mSaveLoading] = useState(false);

	const [sfModalOpen, setSfModalOpen] = useState(false);
	const [zohoStatusLoading, setZohoStatusLoading] = useState(false);
	const [zohoSaveLoading, setZohoSaveLoading] = useState(false);
	const [zohoDisconnectLoading, setZohoDisconnectLoading] = useState(false);
	const [zohoConnected, setZohoConnected] = useState(false);
	const [zohoConfigured, setZohoConfigured] = useState(false);
	const [zohoClientId, setZohoClientId] = useState("");
	const [zohoClientSecret, setZohoClientSecret] = useState("");
	const [zohoDatacenter, setZohoDatacenter] = useState("com");
	const [zohoProspectModule, setZohoProspectModule] = useState("Leads");
	const [zohoProspectSourceField, setZohoProspectSourceField] = useState("Lead_Source");
	const [zohoProspectSourceValue, setZohoProspectSourceValue] = useState("Chatbot");
	const [zohoDescriptionField, setZohoDescriptionField] = useState("Comments");
	const [zohoOwnerId, setZohoOwnerId] = useState("");
	const [zohoTriggerWorkflow, setZohoTriggerWorkflow] = useState(false);
	const [zohoModalOpen, setZohoModalOpen] = useState(false);
	const [nsModalOpen, setNsModalOpen] = useState(false);
	const [wpModalOpen, setWpModalOpen] = useState(false);
	const [wpConnected, setWpConnected] = useState(false);
	const [wpSiteUrl, setWpSiteUrl] = useState("");
	const [wpUsername, setWpUsername] = useState("");

	const [shopifyModalOpen, setShopifyModalOpen] = useState(false);
	const [shopifyConnected, setShopifyConnected] = useState(false);
	const [shopifyShopDomain, setShopifyShopDomain] = useState("");
	const [shopifySiteUrl, setShopifySiteUrl] = useState("");
	const [shopifyOauthReturned, setShopifyOauthReturned] = useState(false);

	const fetchSalesforceStatus = useCallback(async () => {
		if (!websiteId) return;
		setStatusLoading(true);
		try {
			const res = await PostData("ms4", "api/v1/cmsAssistant/salesforce/connection-status", {
				websiteId: Number(websiteId),
			});
			if (res?.status === "success" && res.data) {
				const d = res.data;
				setConnected(!!d.connected);
				setConfigured(!!d.configured);
				setOrgName(d.orgName || "");
				if (d.connected) {
					setSfApiPerms({
						contact: d.contactApi,
						case: d.caseApi,
					});
				} else {
					setSfApiPerms(null);
				}
			} else {
				setSfApiPerms(null);
			}
		} catch (e) {
			console.error(e);
		} finally {
			setStatusLoading(false);
		}
	}, [websiteId]);

	const fetchZohoStatus = useCallback(async () => {
		if (!websiteId) return;
		setZohoStatusLoading(true);
		try {
			const res = await PostData("ms4", "api/v1/cmsAssistant/zoho/connection-status", {
				websiteId: Number(websiteId),
			});
			if (res?.status === "success" && res.data) {
				const d = res.data;
				setZohoConnected(!!d.connected);
				setZohoConfigured(!!d.configured);
				if (d.datacenter) setZohoDatacenter(d.datacenter);
				if (d.prospectModule) setZohoProspectModule(d.prospectModule);
				if (d.prospectSourceField) setZohoProspectSourceField(d.prospectSourceField);
				if (d.prospectSourceValue) setZohoProspectSourceValue(d.prospectSourceValue);
				if (d.descriptionField) setZohoDescriptionField(d.descriptionField);
				if (d.ownerId != null) setZohoOwnerId(String(d.ownerId));
				setZohoTriggerWorkflow(!!d.triggerWorkflow);
			}
		} catch (e) {
			console.error(e);
		} finally {
			setZohoStatusLoading(false);
		}
	}, [websiteId]);

	const fetchNetSuiteStatus = useCallback(async () => {
		if (!websiteId) return;
		setNsStatusLoading(true);
		try {
			const res = await GetRequest(
				"ms1",
				`${NETSUITE_API}status?website_id=${encodeURIComponent(String(websiteId))}`
			);
			if (res?.status === "success" && res.data) {
				const d = res.data;
				setNsConnected(!!d.connected);
				setNsConfigured(!!d.configured);
				setNsRedirectUrl(d.redirectUrl || "");
				setNsAccountMasked(d.accountIdMasked || "");
				setNsTokenExpiresAt(d.tokenExpiresAt || null);
				setNsEnvironment(d.environment === "sandbox" ? "sandbox" : "production");
				setNsAuthMode(d.authMode === "client_credentials" ? "client_credentials" : "authorization_code");
				setNsCertificateExpiresAt(d.certificateExpiresAt || null);
			}
		} catch (e) {
			console.error(e);
		} finally {
			setNsStatusLoading(false);
		}
	}, [websiteId]);

	const fetchWordPressStatus = useCallback(async () => {
		if (!websiteId || !isWordPress) {
			setWpConnected(false);
			setWpSiteUrl("");
			setWpUsername("");
			return;
		}
		try {
			const res = await getWordPressIntegration(websiteId);
			if (res.ok && res.connected && res.integration) {
				setWpConnected(true);
				setWpSiteUrl(res.integration.siteUrl || "");
				setWpUsername(res.integration.username || "");
			} else {
				setWpConnected(false);
				setWpSiteUrl("");
				setWpUsername("");
			}
		} catch (e) {
			console.error(e);
			setWpConnected(false);
			setWpSiteUrl("");
			setWpUsername("");
		}
	}, [websiteId, isWordPress]);

	const fetchShopifyStatus = useCallback(async () => {
		if (!websiteId || !isShopify) {
			setShopifyConnected(false);
			setShopifyShopDomain("");
			setShopifySiteUrl("");
			return;
		}
		try {
			const res = await getShopifyIntegration(websiteId);
			if (res.ok && res.connected && res.integration) {
				setShopifyConnected(true);
				setShopifyShopDomain(res.integration.shopDomain || "");
				setShopifySiteUrl(res.integration.siteUrl || "");
			} else {
				setShopifyConnected(false);
				setShopifyShopDomain("");
				setShopifySiteUrl("");
			}
		} catch (e) {
			console.error(e);
			setShopifyConnected(false);
			setShopifyShopDomain("");
			setShopifySiteUrl("");
		}
	}, [websiteId, isShopify]);

	useEffect(() => {
		if (!userLoader && websiteId) {
			setPageLoader(true);
			const loaders = [fetchSalesforceStatus(), fetchZohoStatus(), fetchNetSuiteStatus()];
			if (isWordPress) loaders.push(fetchWordPressStatus());
			if (isShopify) loaders.push(fetchShopifyStatus());
			Promise.all(loaders).finally(() => setPageLoader(false));
		}
	}, [
		userLoader,
		websiteId,
		isWordPress,
		isShopify,
		fetchSalesforceStatus,
		fetchZohoStatus,
		fetchNetSuiteStatus,
		fetchWordPressStatus,
		fetchShopifyStatus,
	]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const connectedParam = params.get("connected");
		const errorParam = params.get("error");
		if (!connectedParam && !errorParam) return;

		if (connectedParam === "salesforce") {
			toast.success("Salesforce connected successfully.");
			fetchSalesforceStatus();
			setSfModalOpen(true);
		} else if (connectedParam === "zoho") {
			toast.success("Zoho CRM connected successfully.");
			fetchZohoStatus();
			setZohoModalOpen(true);
		} else if (connectedParam === "netsuite") {
			toast.success("NetSuite connected successfully.");
			fetchNetSuiteStatus();
			setNsModalOpen(true);
		} else if (connectedParam === "shopify") {
			toast.success("Shopify authorization complete. Save the integration to finish.");
			setShopifyOauthReturned(true);
			fetchShopifyStatus();
			setShopifyModalOpen(true);
		} else if (errorParam) {
			const sfMsg = OAUTH_ERROR_MESSAGES[errorParam];
			const zohoMsg = ZOHO_OAUTH_ERROR_MESSAGES[errorParam];
			const nsMsg = NETSUITE_OAUTH_ERROR_MESSAGES[errorParam];
			toast.error(sfMsg || zohoMsg || nsMsg || "Connection failed.");
		}
		navigate("/user/integrations", { replace: true });
	}, [location.search, navigate, fetchSalesforceStatus, fetchZohoStatus, fetchNetSuiteStatus, fetchShopifyStatus]);

	const handleSaveCredentials = async (e) => {
		e.preventDefault();
		if (!websiteId) {
			toast.error("No website selected.");
			return;
		}
		if (!clientId.trim() || !clientSecret.trim()) {
			toast.error("Client ID and Client Secret are required.");
			return;
		}
		setSaveLoading(true);
		try {
			const res = await PostData("ms4", "api/v1/cmsAssistant/salesforce/config", {
				websiteId: Number(websiteId),
				clientId: clientId.trim(),
				clientSecret: clientSecret.trim(),
				env,
				caseQueueId: caseQueueId.trim() || undefined,
			});
			if (res?.status === "success") {
				toast.success(res.message || "Credentials saved. You can connect to Salesforce.");
				setConfigured(true);
				await fetchSalesforceStatus();
			} else {
				toast.error(res?.message || "Could not save credentials.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Could not save credentials.");
		} finally {
			setSaveLoading(false);
		}
	};

	const startOAuthConnect = () => {
		if (!websiteId) {
			toast.error("No website selected.");
			return;
		}
		const returnUrl = `${window.location.origin}/user/integrations`;
		const base = Path("ms4");
		const qs = new URLSearchParams({
			websiteId: String(websiteId),
			env,
			returnUrl,
		});
		window.location.href = `${base}api/v1/cmsAssistant/salesforce/oauth/start?${qs.toString()}`;
	};

	const handleZohoSaveCredentials = async (e) => {
		e.preventDefault();
		if (!websiteId) {
			toast.error("No website selected.");
			return;
		}
		if (!zohoClientId.trim() || !zohoClientSecret.trim()) {
			toast.error("Client ID and Client Secret are required.");
			return;
		}
		setZohoSaveLoading(true);
		try {
			const res = await PostData("ms4", "api/v1/cmsAssistant/zoho/config", {
				websiteId: Number(websiteId),
				clientId: zohoClientId.trim(),
				clientSecret: zohoClientSecret.trim(),
				datacenter: zohoDatacenter,
				prospectModule: zohoProspectModule.trim() || "Leads",
				prospectSourceField: zohoProspectSourceField.trim() || "Lead_Source",
				prospectSourceValue: zohoProspectSourceValue.trim() || "Chatbot",
				descriptionField: zohoDescriptionField.trim() || "Comments",
				ownerId: zohoOwnerId.trim() || undefined,
				triggerWorkflow: zohoTriggerWorkflow,
			});
			if (res?.status === "success") {
				toast.success(res.message || "Credentials saved. You can connect to Zoho CRM.");
				setZohoConfigured(true);
				await fetchZohoStatus();
			} else {
				toast.error(res?.message || "Could not save credentials.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Could not save credentials.");
		} finally {
			setZohoSaveLoading(false);
		}
	};

	const startZohoOAuthConnect = () => {
		if (!websiteId) {
			toast.error("No website selected.");
			return;
		}
		const returnUrl = `${window.location.origin}/user/integrations`;
		const base = Path("ms4");
		const qs = new URLSearchParams({
			websiteId: String(websiteId),
			returnUrl,
		});
		window.location.href = `${base}api/v1/cmsAssistant/zoho/oauth/start?${qs.toString()}`;
	};

	const handleZohoDisconnect = async () => {
		if (!websiteId) return;
		setZohoDisconnectLoading(true);
		try {
			const res = await PostData("ms4", "api/v1/cmsAssistant/zoho/disconnect", {
				websiteId: Number(websiteId),
			});
			if (res?.status === "success") {
				toast.success("Disconnected from Zoho CRM.");
				setZohoConnected(false);
				await fetchZohoStatus();
			} else {
				toast.error(res?.message || "Disconnect failed.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Disconnect failed.");
		} finally {
			setZohoDisconnectLoading(false);
		}
	};

	const handleDisconnect = async () => {
		if (!websiteId) return;
		setDisconnectLoading(true);
		try {
			const res = await PostData("ms4", "api/v1/cmsAssistant/salesforce/disconnect", {
				websiteId: Number(websiteId),
			});
			if (res?.status === "success") {
				toast.success("Disconnected from Salesforce.");
				setConnected(false);
				setOrgName("");
				await fetchSalesforceStatus();
			} else {
				toast.error(res?.message || "Disconnect failed.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Disconnect failed.");
		} finally {
			setDisconnectLoading(false);
		}
	};

	const handleNetSuiteSaveAndConnect = async (e) => {
		e.preventDefault();
		if (!websiteId) {
			toast.error("No website selected.");
			return;
		}
		if (!nsClientId.trim() || !nsClientSecret.trim() || !nsAccountId.trim()) {
			toast.error("Client ID, Client Secret, and Account ID are required.");
			return;
		}
		setNsSaveConnectLoading(true);
		try {
			const setupRes = await PostData("ms1", `${NETSUITE_API}setup`, {
				website_id: Number(websiteId),
				client_id: nsClientId.trim(),
				client_secret: nsClientSecret.trim(),
				account_id: nsAccountId.trim(),
				environment: nsEnvironment,
			});
			if (setupRes?.status !== "success") {
				toast.error(setupRes?.message || "Could not save NetSuite credentials.");
				return;
			}
			if (setupRes.data?.redirectUrl) {
				setNsRedirectUrl(setupRes.data.redirectUrl);
			}
			setNsConfigured(true);
			const connectRes = await PostData("ms1", `${NETSUITE_API}connect`, {
				website_id: Number(websiteId),
			});
			if (connectRes?.status !== "success" || !connectRes.data?.authorizeUrl) {
				toast.error(connectRes?.message || "Could not start NetSuite authorization.");
				return;
			}
			window.location.href = connectRes.data.authorizeUrl;
		} catch (err) {
			console.error(err);
			toast.error("Could not start NetSuite connection.");
		} finally {
			setNsSaveConnectLoading(false);
		}
	};

	const handleNetSuiteM2mSetup = async (e) => {
		e.preventDefault();
		if (!websiteId) {
			toast.error("No website selected.");
			return;
		}
		if (!nsClientId.trim() || !nsAccountId.trim()) {
			toast.error("Client ID and Account ID are required before M2M setup.");
			return;
		}
		if (!nsConfigured && !nsClientSecret.trim()) {
			toast.error("Client Secret is required the first time you save integration credentials.");
			return;
		}
		if (!nsCertificateKid.trim() || !nsPrivateKeyPem.trim()) {
			toast.error("Certificate ID and private key PEM are required.");
			return;
		}
		setNsM2mSaveLoading(true);
		try {
			if (!nsConfigured) {
				const setupRes = await PostData("ms1", `${NETSUITE_API}setup`, {
					website_id: Number(websiteId),
					client_id: nsClientId.trim(),
					client_secret: nsClientSecret.trim(),
					account_id: nsAccountId.trim(),
					environment: nsEnvironment,
				});
				if (setupRes?.status !== "success") {
					toast.error(setupRes?.message || "Could not save NetSuite credentials.");
					return;
				}
				setNsConfigured(true);
			}
			const res = await PostData("ms1", `${NETSUITE_API}m2m/setup`, {
				website_id: Number(websiteId),
				certificate_kid: nsCertificateKid.trim(),
				private_key: nsPrivateKeyPem.trim(),
				certificate_pem: nsCertificatePem.trim() || undefined,
			});
			if (res?.status === "success") {
				toast.success(res.message || "M2M configured.");
				setNsPrivateKeyPem("");
				setNsConnected(true);
				setNsAuthMode("client_credentials");
				if (res.data?.certificateExpiresAt) {
					setNsCertificateExpiresAt(res.data.certificateExpiresAt);
				}
				if (res.data?.tokenExpiresAt) {
					setNsTokenExpiresAt(res.data.tokenExpiresAt);
				}
				await fetchNetSuiteStatus();
			} else {
				toast.error(res?.message || "M2M setup failed.");
			}
		} catch (err) {
			console.error(err);
			toast.error("M2M setup failed.");
		} finally {
			setNsM2mSaveLoading(false);
		}
	};

	const handleNetSuitePrivateKeyFile = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => {
			setNsPrivateKeyPem(String(reader.result || ""));
		};
		reader.readAsText(file);
	};

	const handleNetSuiteCertificateFile = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => {
			setNsCertificatePem(String(reader.result || ""));
		};
		reader.readAsText(file);
	};

	const handleNetSuiteTest = async () => {
		if (!websiteId) return;
		setNsTestLoading(true);
		try {
			const res = await GetRequest(
				"ms1",
				`${NETSUITE_API}test?website_id=${encodeURIComponent(String(websiteId))}`
			);
			if (res?.status === "success") {
				toast.success(res.message || "NetSuite connection test succeeded.");
			} else {
				toast.error(res?.message || "NetSuite connection test failed.");
			}
		} catch (err) {
			console.error(err);
			toast.error("NetSuite connection test failed.");
		} finally {
			setNsTestLoading(false);
		}
	};

	const handleNetSuiteDisconnect = async () => {
		if (!websiteId) return;
		setNsDisconnectLoading(true);
		try {
			const res = await PostData("ms1", `${NETSUITE_API}disconnect`, {
				website_id: Number(websiteId),
			});
			if (res?.status === "success") {
				toast.success(res.message || "Disconnected from NetSuite.");
				setNsConnected(false);
				setNsTokenExpiresAt(null);
				await fetchNetSuiteStatus();
			} else {
				toast.error(res?.message || "Disconnect failed.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Disconnect failed.");
		} finally {
			setNsDisconnectLoading(false);
		}
	};

	const copyNsRedirectUrl = async () => {
		if (!nsRedirectUrl) {
			toast.error("Redirect URL is not loaded yet.");
			return;
		}
		try {
			await navigator.clipboard.writeText(nsRedirectUrl);
			toast.success("Redirect URL copied.");
		} catch {
			toast.error("Could not copy. Select the URL and copy manually.");
		}
	};

	const sfStatusBadge = () => {
		if (statusLoading) return <Badge color="light" className="text-muted">Checking…</Badge>;
		if (connected) return <Badge color="success">Connected</Badge>;
		if (configured) return <Badge color="warning">Needs authorization</Badge>;
		return <Badge color="secondary">Not connected</Badge>;
	};

	const zohoStatusBadge = () => {
		if (zohoStatusLoading) return <Badge color="light" className="text-muted">Checking…</Badge>;
		if (zohoConnected) return <Badge color="success">Connected</Badge>;
		if (zohoConfigured) return <Badge color="warning">Needs authorization</Badge>;
		return <Badge color="secondary">Not connected</Badge>;
	};

	const nsIsM2m = nsAuthMode === "client_credentials";

	const nsStatusBadge = () => {
		if (nsStatusLoading) return <Badge color="light" className="text-muted">Checking…</Badge>;
		if (nsConnected && nsIsM2m) return <Badge color="success">Connected (M2M)</Badge>;
		if (nsConnected) return <Badge color="success">Connected</Badge>;
		if (nsConfigured && !nsConnected) return <Badge color="warning">Needs authorization</Badge>;
		return <Badge color="secondary">Not connected</Badge>;
	};

	const nsEnvBadge = () => (
		<Badge color={nsEnvironment === "sandbox" ? "info" : "secondary"} pill className="ms-1">
			{nsEnvironment === "sandbox" ? "Sandbox" : "Production"}
		</Badge>
	);

	const wpStatusBadge = () => {
		if (wpConnected) return <Badge color="success">Connected</Badge>;
		return <Badge color="secondary">Not configured</Badge>;
	};

	const shopifyStatusBadge = () => {
		if (shopifyConnected) return <Badge color="success">Connected</Badge>;
		return <Badge color="secondary">Not configured</Badge>;
	};

	const handleWordPressSaved = (payload) => {
		setWpConnected(true);
		setWpSiteUrl(payload?.siteUrl || "");
		setWpUsername(payload?.username || "");
		toast.success("WordPress integration saved.");
	};

	const handleShopifySaved = (payload) => {
		setShopifyConnected(true);
		setShopifyShopDomain(payload?.shopDomain || "");
		setShopifySiteUrl(payload?.siteUrl || "");
		setShopifyOauthReturned(false);
		toast.success("Shopify integration saved.");
	};

	const nsConnecting = nsConfigured && !nsConnected;

	if (userLoader || pageLoader) {
		return <DetailSkeleton />;
	}

	return (
		<div className="pageContent integrations-page">
			<div className="pageHeader">
				<h2 className="pageTitle">Integrations</h2>
				<p className="text-muted mb-0">
					Connect CRM, ERP, and CMS tools to your website. Choose an app to view status and configure
					credentials.
				</p>
			</div>

			<Row className="g-4 mt-2">
				<Col lg={4} md={6}>
					<Card className="h-100 border-0 shadow-sm integration-tile">
						<CardBody className="d-flex flex-column align-items-center text-center py-4 px-3">
							<IntegrationBrandLogo brand="salesforce" className="mb-3" />
							<h5 className="mb-2">Salesforce</h5>
							<p className="text-muted small mb-3 flex-grow-1">
								Sync your org for contact forms and chat handoffs—create Cases with your Connected App.
							</p>
							<div className="mb-3">{sfStatusBadge()}</div>
							<Button color="primary" className="px-4" onClick={() => setSfModalOpen(true)}>
								{connected || configured ? "Manage" : "Configure"}
							</Button>
						</CardBody>
					</Card>
				</Col>

				<Col lg={4} md={6}>
					<Card className="h-100 border-0 shadow-sm integration-tile">
						<CardBody className="d-flex flex-column align-items-center text-center py-4 px-3">
							<IntegrationBrandLogo brand="zoho" className="mb-3" />
							<h5 className="mb-2">Zoho CRM</h5>
							<p className="text-muted small mb-3 flex-grow-1">
								Send AI shopping assistant contact capture to Zoho Leads with Lead Source Chatbot.
							</p>
							<div className="mb-3">{zohoStatusBadge()}</div>
							<Button color="primary" className="px-4" onClick={() => setZohoModalOpen(true)}>
								{zohoConnected || zohoConfigured ? "Manage" : "Configure"}
							</Button>
						</CardBody>
					</Card>
				</Col>

				<Col lg={4} md={6}>
					<Card className="h-100 border-0 shadow-sm integration-tile">
						<CardBody className="d-flex flex-column align-items-center text-center py-4 px-3">
							<IntegrationBrandLogo brand="netsuite" className="mb-3" />
							<h5 className="mb-2">NetSuite</h5>
							<p className="text-muted small mb-3 flex-grow-1">
								OAuth 2.0 connection per website for REST web services and SuiteTalk APIs.
							</p>
							<div className="mb-3 d-flex flex-wrap justify-content-center gap-1 align-items-center">
								{nsStatusBadge()}
								{!nsStatusLoading && nsEnvBadge()}
							</div>
							<div className="d-flex flex-column gap-2 w-100 align-items-center">
								<Button color="primary" className="px-4" onClick={() => setNsModalOpen(true)}>
									{nsConnected || nsConfigured ? "Manage" : "Configure"}
								</Button>
								{nsConnected && (
									<Button
										tag={Link}
										to="/user/integrations/netsuite-catalog-sync"
										color="outline-primary"
										className="px-4"
									>
										Catalog sync &amp; fields
									</Button>
								)}
							</div>
						</CardBody>
					</Card>
				</Col>

				{isWordPress ? (
					<Col lg={4} md={6}>
						<Card className="h-100 border-0 shadow-sm integration-tile">
							<CardBody className="d-flex flex-column align-items-center text-center py-4 px-3">
								<IntegrationBrandLogo brand="wordpress" className="mb-3" />
								<h5 className="mb-2">WordPress</h5>
								<p className="text-muted small mb-3 flex-grow-1">
									Connect a WordPress site with Application Passwords for secure publishing and content sync.
								</p>
								<div className="mb-3">{wpStatusBadge()}</div>
								{wpConnected && wpSiteUrl ? (
									<p className="text-muted small mb-3 text-truncate w-100" title={wpSiteUrl}>
										{wpSiteUrl}
										{wpUsername ? (
											<>
												<br />
												<span className="text-muted">{wpUsername}</span>
											</>
										) : null}
									</p>
								) : null}
								<Button color="primary" className="px-4" onClick={() => setWpModalOpen(true)}>
									{wpConnected ? "Manage" : "Configure"}
								</Button>
							</CardBody>
						</Card>
					</Col>
				) : null}

				{isShopify ? (
					<Col lg={4} md={6}>
						<Card className="h-100 border-0 shadow-sm integration-tile">
							<CardBody className="d-flex flex-column align-items-center text-center py-4 px-3">
								<IntegrationBrandLogo brand="shopify" className="mb-3" />
								<h5 className="mb-2">Shopify</h5>
								<p className="text-muted small mb-3 flex-grow-1">
									Connect a Shopify store via OAuth for secure publishing and content sync.
								</p>
								<div className="mb-3">{shopifyStatusBadge()}</div>
								{shopifyConnected && (shopifyShopDomain || shopifySiteUrl) ? (
									<p
										className="text-muted small mb-3 text-truncate w-100"
										title={shopifyShopDomain || shopifySiteUrl}
									>
										{shopifyShopDomain || shopifySiteUrl}
										{shopifyShopDomain && shopifySiteUrl ? (
											<>
												<br />
												<span className="text-muted">{shopifySiteUrl}</span>
											</>
										) : null}
									</p>
								) : null}
								<Button
									color="primary"
									className="px-4"
									onClick={() => {
										setShopifyOauthReturned(false);
										setShopifyModalOpen(true);
									}}
								>
									{shopifyConnected ? "Manage" : "Configure"}
								</Button>
							</CardBody>
						</Card>
					</Col>
				) : null}
			</Row>

			<Modal
				isOpen={sfModalOpen}
				toggle={() => setSfModalOpen(false)}
				size="lg"
				scrollable
				className="integration-modal"
				contentClassName="border-0 rounded"
			>
				<ModalHeader toggle={() => setSfModalOpen(false)} className="border-bottom">
					Salesforce
				</ModalHeader>
				<ModalBody className="pt-4 px-4" style={{ maxHeight: "min(75vh, 640px)", overflowY: "auto", flexDirection: "column" }}>
					<p className="text-muted small mb-4 lh-base">
						Connect a Salesforce org so contact form and chat handoffs can create Cases using your Connected App.
					</p>

					<section className="mb-4 pb-4 border-bottom">
						<div className="small fw-semibold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.04em" }}>
							Status
						</div>
						{statusLoading ? (
							<p className="text-muted mb-0">Checking…</p>
						) : (
							<>
								{connected ? (
									<>
										<Alert color="success" className="mb-2 py-2 small">
											Connected
											{orgName ? ` — ${orgName}` : ""}
										</Alert>
										{sfApiPerms &&
											(sfApiPerms.contact?.createable === false || sfApiPerms.case?.createable === false) && (
												<Alert color="warning" className="mb-0 py-2 small">
													The Salesforce user that authorized this connection cannot create{" "}
													{sfApiPerms.contact?.createable === false && sfApiPerms.case?.createable === false
														? "Contacts or Cases"
														: sfApiPerms.contact?.createable === false
															? "Contacts"
															: "Cases"}{" "}
													via the API. In Salesforce Setup, give that user&apos;s Profile or Permission Set{" "}
													<strong>Create</strong> (and Read) on Contact and Case, then disconnect and connect
													again.
												</Alert>
											)}
									</>
								) : configured ? (
									<Alert color="warning" className="mb-0 py-2 small">
										Credentials saved — authorize to finish connecting.
									</Alert>
								) : (
									<Alert color="light" className="mb-0 py-2 small text-dark border">
										Not configured — add your Connected App credentials below, then save.
									</Alert>
								)}
							</>
						)}
					</section>

					<section className="mb-4 pb-4 border-bottom">
						<div className="small fw-semibold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.04em" }}>
							Authorize
						</div>
						<div className="d-grid gap-2 d-sm-flex flex-sm-row flex-sm-wrap">
							<Button
								color="primary"
								className="text-nowrap"
								disabled={!configured || statusLoading || connected}
								onClick={startOAuthConnect}
							>
								Connect to Salesforce
							</Button>
							<Button
								color="outline-secondary"
								className="text-nowrap"
								disabled={!connected || disconnectLoading}
								onClick={handleDisconnect}
							>
								{disconnectLoading ? "Disconnecting…" : "Disconnect"}
							</Button>
						</div>
						<p className="text-muted small mt-3 mb-0 lh-base">
							Use the same environment (Production / Sandbox) as your Salesforce login and Connected App.
						</p>
					</section>

					<section>
						<div className="small fw-semibold text-uppercase text-muted mb-3" style={{ letterSpacing: "0.04em" }}>
							Connected App credentials
						</div>
						<form id="integration-sf-credentials-form" onSubmit={handleSaveCredentials}>
							<Row className="g-3">
								<Col xs={12}>
									<Label for="sf-modal-client-id" className="small fw-semibold mb-1 d-block">
										Client ID (Consumer Key)
									</Label>
									<Input
										id="sf-modal-client-id"
										type="text"
										className="w-100"
										value={clientId}
										onChange={(e) => setClientId(e.target.value)}
										autoComplete="off"
									/>
								</Col>
								<Col xs={12}>
									<Label for="sf-modal-client-secret" className="small fw-semibold mb-1 d-block">
										Client Secret
									</Label>
									<Input
										id="sf-modal-client-secret"
										type="password"
										className="w-100"
										value={clientSecret}
										onChange={(e) => setClientSecret(e.target.value)}
										autoComplete="new-password"
										placeholder="Consumer Secret"
									/>
								</Col>
								<Col xs={12} sm={6}>
									<Label for="sf-modal-env" className="small fw-semibold mb-1 d-block">
										Environment
									</Label>
									<Input
										id="sf-modal-env"
										type="select"
										className="w-100"
										value={env}
										onChange={(e) => setEnv(e.target.value)}
									>
										<option value="production">Production</option>
										<option value="sandbox">Sandbox</option>
									</Input>
								</Col>
								<Col xs={12} sm={6}>
									<Label for="sf-modal-queue" className="small fw-semibold mb-1 d-block">
										Case queue ID (optional)
									</Label>
									<Input
										id="sf-modal-queue"
										type="text"
										className="w-100"
										value={caseQueueId}
										onChange={(e) => setCaseQueueId(e.target.value)}
										placeholder="e.g. assign new Cases to a queue"
									/>
								</Col>
							</Row>
						</form>
					</section>
				</ModalBody>
				<ModalFooter className="border-top px-4 py-3 bg-light">
					<Button color="secondary" outline type="button" onClick={() => setSfModalOpen(false)}>
						Close
					</Button>
					<Button
						color="primary"
						type="submit"
						form="integration-sf-credentials-form"
						disabled={saveLoading}
					>
						{saveLoading ? "Saving…" : "Save credentials"}
					</Button>
				</ModalFooter>
			</Modal>

			<Modal
				isOpen={zohoModalOpen}
				toggle={() => setZohoModalOpen(false)}
				size="lg"
				scrollable
				className="integration-modal"
				contentClassName="border-0 rounded"
			>
				<ModalHeader toggle={() => setZohoModalOpen(false)} className="border-bottom">
					Zoho CRM
				</ModalHeader>
				<ModalBody className="pt-4 px-4" style={{ maxHeight: "min(75vh, 640px)", overflowY: "auto", flexDirection: "column" }}>
					<p className="text-muted small mb-4 lh-base">
						Connect Zoho CRM so offline messages and live-chat handoffs create Leads with Lead Source set to
						Chatbot (field names configurable below).
					</p>

					<section className="mb-4 pb-4 border-bottom">
						<div className="small fw-semibold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.04em" }}>
							Status
						</div>
						{zohoStatusLoading ? (
							<p className="text-muted mb-0">Checking…</p>
						) : zohoConnected ? (
							<Alert color="success" className="mb-0 py-2 small">
								Connected — module {zohoProspectModule || "Leads"}
							</Alert>
						) : zohoConfigured ? (
							<Alert color="warning" className="mb-0 py-2 small">
								Credentials saved — authorize to finish connecting.
							</Alert>
						) : (
							<Alert color="light" className="mb-0 py-2 small text-dark border">
								Not configured — add your Zoho Connected App credentials below, then save.
							</Alert>
						)}
					</section>

					<section className="mb-4 pb-4 border-bottom">
						<div className="small fw-semibold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.04em" }}>
							Authorize
						</div>
						<div className="d-grid gap-2 d-sm-flex flex-sm-row flex-sm-wrap">
							<Button
								color="primary"
								className="text-nowrap"
								disabled={!zohoConfigured || zohoStatusLoading || zohoConnected}
								onClick={startZohoOAuthConnect}
							>
								Connect to Zoho CRM
							</Button>
							<Button
								color="outline-secondary"
								className="text-nowrap"
								disabled={!zohoConnected || zohoDisconnectLoading}
								onClick={handleZohoDisconnect}
							>
								{zohoDisconnectLoading ? "Disconnecting…" : "Disconnect"}
							</Button>
						</div>
						<p className="text-muted small mt-3 mb-0 lh-base">
							Use the redirect URL from your Zoho Connected App:{" "}
							<code className="small">{Path("ms4")}api/v1/cmsAssistant/zoho/oauth/callback</code>
						</p>
					</section>

					<section>
						<div className="small fw-semibold text-uppercase text-muted mb-3" style={{ letterSpacing: "0.04em" }}>
							Connected App &amp; Lead mapping
						</div>
						<form id="integration-zoho-credentials-form" onSubmit={handleZohoSaveCredentials}>
							<Row className="g-3">
								<Col xs={12}>
									<Label for="zoho-modal-client-id" className="small fw-semibold mb-1 d-block">
										Client ID
									</Label>
									<Input
										id="zoho-modal-client-id"
										type="text"
										className="w-100"
										value={zohoClientId}
										onChange={(e) => setZohoClientId(e.target.value)}
										autoComplete="off"
									/>
								</Col>
								<Col xs={12}>
									<Label for="zoho-modal-client-secret" className="small fw-semibold mb-1 d-block">
										Client Secret
									</Label>
									<Input
										id="zoho-modal-client-secret"
										type="password"
										className="w-100"
										value={zohoClientSecret}
										onChange={(e) => setZohoClientSecret(e.target.value)}
										autoComplete="new-password"
									/>
								</Col>
								<Col xs={12} sm={6}>
									<Label for="zoho-modal-dc" className="small fw-semibold mb-1 d-block">
										Data center
									</Label>
									<Input
										id="zoho-modal-dc"
										type="select"
										className="w-100"
										value={zohoDatacenter}
										onChange={(e) => setZohoDatacenter(e.target.value)}
									>
										<option value="com">United States (.com)</option>
										<option value="eu">Europe (.eu)</option>
										<option value="in">India (.in)</option>
										<option value="au">Australia (.com.au)</option>
									</Input>
								</Col>
								<Col xs={12} sm={6}>
									<Label for="zoho-modal-module" className="small fw-semibold mb-1 d-block">
										Zoho module API name
									</Label>
									<Input
										id="zoho-modal-module"
										type="text"
										className="w-100"
										value={zohoProspectModule}
										onChange={(e) => setZohoProspectModule(e.target.value)}
										placeholder="Leads"
									/>
								</Col>
								<Col xs={12} sm={6}>
									<Label for="zoho-modal-source-field" className="small fw-semibold mb-1 d-block">
										Lead Source field API name
									</Label>
									<Input
										id="zoho-modal-source-field"
										type="text"
										className="w-100"
										value={zohoProspectSourceField}
										onChange={(e) => setZohoProspectSourceField(e.target.value)}
										placeholder="Lead_Source"
									/>
								</Col>
								<Col xs={12} sm={6}>
									<Label for="zoho-modal-source-value" className="small fw-semibold mb-1 d-block">
										Lead Source value
									</Label>
									<Input
										id="zoho-modal-source-value"
										type="text"
										className="w-100"
										value={zohoProspectSourceValue}
										onChange={(e) => setZohoProspectSourceValue(e.target.value)}
										placeholder="Chatbot"
									/>
								</Col>
								<Col xs={12} sm={6}>
									<Label for="zoho-modal-desc-field" className="small fw-semibold mb-1 d-block">
										Comments / description field
									</Label>
									<Input
										id="zoho-modal-desc-field"
										type="text"
										className="w-100"
										value={zohoDescriptionField}
										onChange={(e) => setZohoDescriptionField(e.target.value)}
										placeholder="Comments"
									/>
								</Col>
								<Col xs={12} sm={6}>
									<Label for="zoho-modal-owner" className="small fw-semibold mb-1 d-block">
										Owner ID (optional)
									</Label>
									<Input
										id="zoho-modal-owner"
										type="text"
										className="w-100"
										value={zohoOwnerId}
										onChange={(e) => setZohoOwnerId(e.target.value)}
										placeholder="Zoho user id"
									/>
								</Col>
								<Col xs={12}>
									<div className="form-check">
										<Input
											id="zoho-modal-trigger-workflow"
											type="checkbox"
											className="form-check-input"
											checked={zohoTriggerWorkflow}
											onChange={(e) => setZohoTriggerWorkflow(e.target.checked)}
										/>
										<Label for="zoho-modal-trigger-workflow" className="form-check-label small">
											Trigger Zoho workflows on create
										</Label>
									</div>
								</Col>
							</Row>
						</form>
					</section>
				</ModalBody>
				<ModalFooter className="border-top px-4 py-3 bg-light">
					<Button color="secondary" outline type="button" onClick={() => setZohoModalOpen(false)}>
						Close
					</Button>
					<Button
						color="primary"
						type="submit"
						form="integration-zoho-credentials-form"
						disabled={zohoSaveLoading}
					>
						{zohoSaveLoading ? "Saving…" : "Save credentials"}
					</Button>
				</ModalFooter>
			</Modal>

			<Modal
				isOpen={nsModalOpen}
				toggle={() => setNsModalOpen(false)}
				size="lg"
				scrollable
				className="integration-modal"
				contentClassName="border-0 rounded"
			>
				<ModalHeader toggle={() => setNsModalOpen(false)} className="border-bottom">
					NetSuite
				</ModalHeader>
				<ModalBody className="pt-4 px-4" style={{ maxHeight: "min(75vh, 640px)", overflowY: "auto", flexDirection: "column" }}>
					<p className="text-muted small mb-4 lh-base">
						Connect NetSuite REST web services for this website. Each website has its own OAuth credentials and
						tokens.
					</p>

					<section className="mb-4 pb-4 border-bottom">
						<div className="small fw-semibold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.04em" }}>
							Status
						</div>
						{nsStatusLoading ? (
							<p className="text-muted mb-0">Checking…</p>
						) : nsConnected ? (
							<Alert color="success" className="mb-0 py-2 small">
								<span className="me-2">{nsIsM2m ? "Connected (M2M)" : "Connected"}</span>
								<Badge color="light" className="text-dark border">
									{nsEnvironment === "sandbox" ? "Sandbox" : "Production"}
								</Badge>
								{nsAccountMasked ? ` — Account ${nsAccountMasked}` : ""}
								{nsTokenExpiresAt && (
									<span className="d-block small mt-1">
										Access token expires: {new Date(nsTokenExpiresAt).toLocaleString()}
									</span>
								)}
								{nsCertificateExpiresAt && (
									<span className="d-block small mt-1">
										Certificate expires: {new Date(nsCertificateExpiresAt).toLocaleString()}
									</span>
								)}
							</Alert>
						) : nsConnecting ? (
							<Alert color="warning" className="mb-0 py-2 small">
								Connecting — complete sign-in at NetSuite, or use Save &amp; Connect again.
							</Alert>
						) : (
							<Alert color="light" className="mb-0 py-2 small text-dark border">
								Not connected — add your NetSuite integration credentials below.
							</Alert>
						)}
					</section>

					<section className="mb-4 pb-4 border-bottom">
						<div className="small fw-semibold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.04em" }}>
							Authentication mode
						</div>
						<Input
							type="select"
							className="w-100"
							value={nsAuthMode}
							onChange={(e) => setNsAuthMode(e.target.value)}
							disabled={nsConnected}
						>
							<option value="authorization_code">User OAuth (legacy refresh token)</option>
							<option value="client_credentials">Server M2M (certificate, recommended)</option>
						</Input>
						<p className="text-muted small mb-0 mt-2">
							<strong>M2M</strong> uses a certificate (up to 2 years) for background sync without periodic user login.
							Enable Client Credentials on your NetSuite integration and map the certificate to a role.
						</p>
					</section>

					{nsAuthMode === "client_credentials" ? (
						<section className="mb-4 pb-4 border-bottom">
							<div className="small fw-semibold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.04em" }}>
								M2M certificate
							</div>
							<form id="integration-ns-m2m-form" onSubmit={handleNetSuiteM2mSetup}>
								<Row className="g-3">
									<Col xs={12}>
										<Label for="ns-modal-certificate-kid" className="small fw-semibold mb-1 d-block">
											Certificate ID (kid)
										</Label>
										<Input
											id="ns-modal-certificate-kid"
											type="text"
											className="w-100 font-monospace small"
											value={nsCertificateKid}
											onChange={(e) => setNsCertificateKid(e.target.value)}
											placeholder="From NetSuite M2M certificate mapping"
											autoComplete="off"
										/>
									</Col>
									<Col xs={12}>
										<Label for="ns-modal-private-key" className="small fw-semibold mb-1 d-block">
											Private key (PEM)
										</Label>
										<Input
											id="ns-modal-private-key-file"
											type="file"
											accept=".pem,.key,.txt"
											onChange={handleNetSuitePrivateKeyFile}
											className="mb-2"
										/>
										<Input
											id="ns-modal-private-key"
											type="textarea"
											rows={4}
											className="w-100 font-monospace small"
											value={nsPrivateKeyPem}
											onChange={(e) => setNsPrivateKeyPem(e.target.value)}
											placeholder="-----BEGIN PRIVATE KEY-----"
										/>
									</Col>
									<Col xs={12}>
										<Label className="small fw-semibold mb-1 d-block">
											Public certificate (optional, for expiry display)
										</Label>
										<Input
											type="file"
											accept=".pem,.crt,.cer"
											onChange={handleNetSuiteCertificateFile}
										/>
									</Col>
								</Row>
							</form>
						</section>
					) : null}

					<section className="mb-4 pb-4 border-bottom">
						<div className="small fw-semibold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.04em" }}>
							Integration credentials
						</div>
						<p className="text-muted small mb-3">
							<span id="netsuite-help-modal" className="text-muted">
								Create an integration in NetSuite and paste Client ID, Secret, and Account ID.
							</span>
							<UncontrolledTooltip placement="right" target="netsuite-help-modal">
								In NetSuite, create an OAuth 2.0 integration, enable REST Web Services, and add the redirect URL
								below to the callback list.
							</UncontrolledTooltip>
						</p>
						<form id="integration-ns-form" onSubmit={handleNetSuiteSaveAndConnect}>
							<Row className="g-3">
								<Col xs={12} sm={6}>
									<Label for="ns-modal-environment" className="small fw-semibold mb-1 d-block">
										Environment
									</Label>
									<Input
										id="ns-modal-environment"
										type="select"
										className="w-100"
										value={nsEnvironment}
										onChange={(e) => setNsEnvironment(e.target.value)}
									>
										<option value="production">Production</option>
										<option value="sandbox">Sandbox</option>
									</Input>
								</Col>
								<Col xs={12} sm={6}>
									<Label for="ns-modal-account-id" className="small fw-semibold mb-1 d-block">
										Account ID
									</Label>
									<Input
										id="ns-modal-account-id"
										type="text"
										className="w-100"
										value={nsAccountId}
										onChange={(e) => setNsAccountId(e.target.value)}
										placeholder={
											nsEnvironment === "sandbox"
												? "e.g. 1234567_SB1"
												: "e.g. 1234567"
										}
										autoComplete="off"
									/>
								</Col>
								<Col xs={12}>
									<p className="text-muted small mb-0">
										Choose <strong>Sandbox</strong> when connecting a NetSuite sandbox account (account ID
										usually includes <code className="small">_SB</code>).
									</p>
								</Col>
								<Col xs={12}>
									<Label for="ns-modal-client-id" className="small fw-semibold mb-1 d-block">
										Client ID
									</Label>
									<Input
										id="ns-modal-client-id"
										type="text"
										className="w-100"
										value={nsClientId}
										onChange={(e) => setNsClientId(e.target.value)}
										autoComplete="off"
									/>
								</Col>
								<Col xs={12}>
									<Label for="ns-modal-client-secret" className="small fw-semibold mb-1 d-block">
										Client Secret
									</Label>
									<Input
										id="ns-modal-client-secret"
										type="password"
										className="w-100"
										value={nsClientSecret}
										onChange={(e) => setNsClientSecret(e.target.value)}
										autoComplete="new-password"
									/>
								</Col>
							</Row>
						</form>
					</section>

					<section className="mb-4 pb-4 border-bottom">
						<div className="small fw-semibold text-uppercase text-muted mb-2" style={{ letterSpacing: "0.04em" }}>
							Redirect URL
						</div>
						<p className="text-muted small mb-3">
							Register this exact callback URL in your NetSuite integration (per website).
						</p>
						<div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-start">
							<Input
								readOnly
								className="font-monospace small flex-grow-1 w-100"
								value={nsRedirectUrl || "Loading…"}
								onFocus={(e) => e.target.select()}
							/>
							<Button color="outline-secondary" type="button" className="align-self-sm-start" onClick={copyNsRedirectUrl}>
								Copy
							</Button>
						</div>
					</section>

					<section>
						<div className="small fw-semibold text-uppercase text-muted mb-3" style={{ letterSpacing: "0.04em" }}>
							Actions
						</div>
						<div className="d-grid gap-2 d-sm-flex flex-sm-row flex-sm-wrap">
							{nsAuthMode === "client_credentials" ? (
								<Button
									color="primary"
									type="submit"
									form="integration-ns-m2m-form"
									className="text-nowrap"
									disabled={nsM2mSaveLoading || nsStatusLoading}
								>
									{nsM2mSaveLoading ? "Saving…" : "Save & verify M2M"}
								</Button>
							) : (
								<Button
									color="primary"
									type="submit"
									form="integration-ns-form"
									className="text-nowrap"
									disabled={nsSaveConnectLoading || nsStatusLoading}
								>
									{nsSaveConnectLoading ? "Saving…" : "Save & Connect"}
								</Button>
							)}
							<Button
								color="outline-primary"
								type="button"
								className="text-nowrap"
								disabled={!nsConnected || nsTestLoading || nsStatusLoading}
								onClick={handleNetSuiteTest}
							>
								{nsTestLoading ? "Testing…" : "Test connection"}
							</Button>
							<Button
								color="outline-secondary"
								type="button"
								className="text-nowrap"
								disabled={!nsConnected || nsDisconnectLoading || nsStatusLoading}
								onClick={handleNetSuiteDisconnect}
							>
								{nsDisconnectLoading ? "Disconnecting…" : "Disconnect"}
							</Button>
						</div>
					</section>

					{nsConnected && (
						<section className="mb-0">
							<Alert color="light" className="small mb-0 py-2 border">
								<strong>Catalog sync</strong> — configure polling, Search/AI targets, and field mappings on the{" "}
								<Link to="/user/integrations/netsuite-catalog-sync" className="alert-link fw-semibold">
									NetSuite catalog sync
								</Link>{" "}
								page.
							</Alert>
						</section>
					)}
				</ModalBody>
				<ModalFooter className="border-top px-4 py-3 bg-light">
					<Button color="secondary" outline type="button" onClick={() => setNsModalOpen(false)}>
						Close
					</Button>
				</ModalFooter>
			</Modal>

			{isWordPress ? (
				<WordPressIntegrationModal
					isOpen={wpModalOpen}
					onClose={() => setWpModalOpen(false)}
					websiteId={websiteId}
					siteUrl={
						websiteData?.[websiteId]?.websiteURL ||
						websiteData?.[websiteId]?.websiteUrl ||
						wpSiteUrl ||
						""
					}
					onSaved={handleWordPressSaved}
				/>
			) : null}

			{isShopify ? (
				<ShopifyIntegrationModal
					isOpen={shopifyModalOpen}
					onClose={() => {
						setShopifyModalOpen(false);
						setShopifyOauthReturned(false);
					}}
					websiteId={websiteId}
					connected={shopifyConnected}
					oauthJustReturned={shopifyOauthReturned}
					shopDomain={shopifyShopDomain}
					siteUrl={
						shopifySiteUrl ||
						websiteData?.[websiteId]?.websiteURL ||
						websiteData?.[websiteId]?.websiteUrl ||
						""
					}
					onSaved={handleShopifySaved}
				/>
			) : null}
		</div>
	);
};

export default Integrations;
